import { useState, useCallback } from 'react';
import { 
  useOnBoardingService, 
  Tenant, 
  User, 
  TenantFeature 
} from '../services/onBoardingService';

/**
 * Custom hook for managing tenant operations in onboarding components
 */
export const useOnBoarding = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const onBoardingService = useOnBoardingService();

  /**
   * Load all tenants
   */
  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await onBoardingService.getTenants();
      setTenants(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tenants';
      setError(errorMessage);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [onBoardingService]);

  /**
   * Get users for a specific tenant
   */
  const getTenantUsers = useCallback(async (tenantId: string): Promise<User[]> => {
    try {
      return await onBoardingService.getTenantUsers(tenantId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tenant users';
      setError(errorMessage);
      return [];
    }
  }, [onBoardingService]);

  /**
   * Get available features
   */
  const getAvailableFeatures = useCallback(async (): Promise<TenantFeature[]> => {
    try {
      return await onBoardingService.getAvailableFeatures();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load features';
      setError(errorMessage);
      return [];
    }
  }, [onBoardingService]);

  /**
   * Update tenant feature mapping
   */
  const updateTenantFeatures = useCallback(async (
    tenantId: string,
    features: string[]
  ): Promise<boolean> => {
    try {
      await onBoardingService.updateTenantFeatures(tenantId, features);
      await loadTenants(); // Reload to get updated data
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tenant features';
      setError(errorMessage);
      return false;
    }
  }, [onBoardingService, loadTenants]);

  /**
   * Update tenant status
   */
  const updateTenantStatus = useCallback(async (
    tenantId: string,
    status: 'Active' | 'Inactive'
  ): Promise<boolean> => {
    try {
      await onBoardingService.updateTenantStatus(tenantId, status);
      await loadTenants(); // Reload to get updated data
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tenant status';
      setError(errorMessage);
      return false;
    }
  }, [onBoardingService, loadTenants]);

  /**
   * Refresh tenant data
   */
  const refreshTenants = useCallback(() => {
    loadTenants();
  }, [loadTenants]);

  return {
    // State
    tenants,
    loading,
    error,
    
    // Methods
    loadTenants,
    refreshTenants,
    getTenantUsers,
    getAvailableFeatures,
    updateTenantFeatures,
    updateTenantStatus,
    
    // Utility
    clearError: () => setError(null),
  };
};